// app/frontend/entrypoints/inertia.ts
import { createInertiaApp, router } from "@inertiajs/react";
import { createElement, ReactNode, ComponentType, Fragment } from "react";
import { createRoot } from "react-dom/client";
import { MantineProvider, createTheme } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { l2kNotify } from "../components/ui/Feedback";
import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
// App CSS: brings in tokens.css (via @import) and the shared
// .markdown-body / .blog-* rules. Without this import the entire
// stylesheet is orphaned and Vite's production build skips it.
import "./application.css";

// ---- Types ----
type PageComponent = ComponentType & {
  layout?: (page: ReactNode) => ReactNode;
};

type PageModule = {
  default: PageComponent;
};

// ---- Theme ----
const theme = createTheme({
  colors: {
    yellow: [
      "#fff9db",
      "#fff3bf",
      "#ffec99",
      "#ffe066",
      "#ffd43b",
      "#fcc419",
      "#fab005",
      "#f59f00",
      "#f08c00",
      "#e67700",
    ],
    red: [
      "#ffe0e1",
      "#ffb3b3",
      "#ff8080",
      "#ff4d4d",
      "#ff1a1a",
      "#e60000",
      "#cc0000",
      "#b30000",
      "#990000",
      "#800000",
    ],
  },
  primaryColor: "yellow",
  fontFamily:
    "'Public Sans', -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  headings: {
    fontFamily:
      "'Big Shoulders Display', -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
});

// Surface Rails flash (shared as an Inertia prop in ApplicationController)
// as L2K-styled notifications on every successful visit — no per-page wiring.
router.on("success", (event) => {
  const flash = (event.detail?.page?.props as { flash?: { notice?: string | null; alert?: string | null } })?.flash;
  if (!flash) return;
  if (flash.notice) l2kNotify.success(flash.notice);
  if (flash.alert)  l2kNotify.error(flash.alert);
});

createInertiaApp({
  resolve: (name) => {
    // Import pages eagerly with type safety
    const pages = import.meta.glob<PageModule>("../pages/**/*.tsx", {
      eager: true,
    });

    const mod = pages[`../pages/${name}.tsx`];
    if (!mod) {
      console.error(`Missing Inertia page component: '${name}.tsx'`);
      return { default: (() => null) as PageComponent };
    }

    // Ensure a layout wrapper exists
    mod.default.layout ??= (page: ReactNode) => page;
    return mod;
  },

  setup({ el, App, props }) {
    if (!el) {
      console.error(
        "Missing root element.\n\n" +
          "If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n" +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.'
      );
      return;
    }

    const root = createRoot(el);

    // Wrap Inertia App in MantineProvider with Notifications
    root.render(
      createElement(
        MantineProvider,
        { theme, defaultColorScheme: "dark" },
        createElement(
          Fragment,
          null,
          createElement(Notifications, { position: "top-right" }),
          createElement(App, props)
        )
      )
    );
  },
});
